import { CaretLeft } from "components/icons/CaretLeft";
import { CLICKED_YEAR_SWITCHER_ON_FILINGS_TIMELINE } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { TimelineFilingRow } from "store/apis/filingsTimeline";
import { TimelineLegend } from "./TimelineLegend";
import { TimelineYearMenu } from "./TimelineYearMenu";
import classNames from "classnames";

const ARROW_CLASS = (isDisabled: boolean) => ({
  "t-text-neutral-30": isDisabled,
  "t-text-text-30": !isDisabled,
});

type TimelineHeaderProps = {
  viewYear: number;
  filings: TimelineFilingRow[];
  years: number[];
  minYear: number;
  maxYear: number;
  hasAnyFiling: boolean;
  onChangeYear: (year: number) => void;
};

export const TimelineHeader = ({
  viewYear,
  filings,
  years,
  minYear,
  maxYear,
  hasAnyFiling,
  onChangeYear,
}: TimelineHeaderProps) => {
  const { trackEvent } = useAnalytics();

  const handleChangeYear = (year: number) => {
    trackEvent(CLICKED_YEAR_SWITCHER_ON_FILINGS_TIMELINE, { year });
    onChangeYear(year);
  };

  const goToPreviousYear = () => handleChangeYear(viewYear - 1);
  const goToNextYear = () => handleChangeYear(viewYear + 1);

  const isAtEarliestYear = viewYear <= minYear;
  const isAtLatestYear = viewYear >= maxYear;

  return (
    <div className="t-flex t-flex-wrap t-items-center t-justify-between t-gap-x-4 t-gap-y-2 t-px-5 t-pb-5 t-pt-[18px]">
      <div className="t-flex t-flex-col t-items-center t-gap-0.5">
        <span className="t-text-caption t-text-neutral-40">Calendar year</span>

        <div className="t-flex t-items-center t-gap-1">
          <button
            onClick={goToPreviousYear}
            disabled={isAtEarliestYear}
            aria-label="Previous year"
            className="all:unset"
          >
            <span
              className={classNames("t-flex", ARROW_CLASS(isAtEarliestYear))}
            >
              <CaretLeft width="12" height="12" />
            </span>
          </button>
          <span className="t-text-subtitle t-text-text-60">{viewYear}</span>
          <button
            onClick={goToNextYear}
            disabled={isAtLatestYear}
            aria-label="Next year"
            className="all:unset"
          >
            <span
              className={classNames(
                "t-flex t-rotate-180",
                ARROW_CLASS(isAtLatestYear)
              )}
            >
              <CaretLeft width="12" height="12" />
            </span>
          </button>
        </div>
      </div>

      {hasAnyFiling && (
        <div className="t-flex t-items-center t-gap-4">
          <TimelineLegend filings={filings} />
          <TimelineYearMenu
            years={years}
            viewYear={viewYear}
            onSelectYear={handleChangeYear}
          />
        </div>
      )}
    </div>
  );
};
